import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import CustomPagination from "app/widgets/Shared/Pagination";
import deviceServices from "app/services/automations/device-services";
import DeviceCard from "app/shared/devices/DeviceCard";
import DeviceDetailPanel from "app/shared/devices/DeviceDetailPanel";
import DeviceEditModal from "./DeviceEditModal";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { hasProfile } from "app/utils/appHelpers";
import { StandardFiltersBar, StandardPageHeader, StandardSearchField } from "app/shared/ui";

const DevicesIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [recordsList, setRecordsList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [listLoading, setListLoading] = useState(false);
    const [openRecordEditModal, setOpenRecordEditModal] = useState(false);
    const [currentDeviceID, setCurrentDeviceID] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ stores: [], zones: [], kinds: [] });
    const [filters, setFilters] = useState({ storeId: "", zone: "", kind: "" });
    const [searchParams, setSearchParams] = useSearchParams();
    const canCreateDevice = hasProfile(authUser, 'superadmin');
    const openedDeviceId = searchParams.get("device");

    const openDeviceDetail = useCallback((deviceId) => {
        if (!deviceId) return;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("device", `${deviceId}`);
        setSearchParams(nextParams);
    }, [searchParams, setSearchParams]);

    const closeDeviceDetail = useCallback(() => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("device");
        setSearchParams(nextParams);
    }, [searchParams, setSearchParams]);

    const existingZoneOptions = useMemo(() => {
        if (filterOptions.zones?.length) {
            return filterOptions.zones;
        }
        return _.uniq(_.compact(_.map(recordsList, "zone"))).sort((a, b) => a.localeCompare(b));
    }, [filterOptions.zones, recordsList]);

    const buildRecordParams = useCallback(() => {
        const params = [
            `page=${currentPage}`,
            "include=device_type.measurement_types,latest_device_metric,store",
            encodeURI("q[s][]=store_id asc"),
            encodeURI("q[s][]=zone asc"),
            encodeURI("q[s][]=name asc"),
        ];

        if (searchQuery?.trim()) {
            params.push(encodeURI(`q[name_or_kind_or_zone_cont]=${searchQuery.trim()}`));
        }
        if (filters.storeId) {
            params.push(encodeURI(`q[store_id_eq]=${filters.storeId}`));
        }
        if (filters.zone) {
            params.push(encodeURI(`q[zone_eq]=${filters.zone}`));
        }
        if (filters.kind) {
            params.push(encodeURI(`q[kind_eq]=${filters.kind}`));
        }

        return `?${params.join("&")}`;
    }, [currentPage, filters.kind, filters.storeId, filters.zone, searchQuery]);

    const loadRecords = useCallback(() => {
        deviceServices.getDevices(buildRecordParams())
            .then((resp) => {
                setRecordsList(resp?.data || []);
                setTotalPages(resp?.meta?.total_pages || 1);
                setTotalCount(resp?.meta?.total_count || 0);
            })
            .catch(() => {})
            .finally(() => {
                setListLoading(false);
            });
    }, [buildRecordParams]);

    useEffect(() => {
        if (listLoading) {
            loadRecords();
        }
    }, [listLoading, loadRecords]);

    const loadFilterOptions = useCallback(() => {
        deviceServices.getEditOptions()
            .then((resp) => {
                const stores = _.orderBy(resp?.stores || [], ["name"], ["asc"]);
                const zones = _.orderBy(resp?.zones || [], [(z) => (z || "").toString().toLowerCase()], ["asc"]);
                const kinds = _.orderBy(resp?.kinds || [], [(k) => (k || "").toString().toLowerCase()], ["asc"]);
                setFilterOptions({
                    stores,
                    zones,
                    kinds,
                });
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        loadFilterOptions();
        setListLoading(true);
    }, [loadFilterOptions]);

    const normalizeDeviceResponse = (payload) => {
        if (payload?.data?.id && payload?.data?.attributes) {
            return {
                id: payload.data.id,
                ...payload.data.attributes,
            };
        }
        return payload;
    };

    const handleRecordChange = (newRec) => {
        const normalized = normalizeDeviceResponse(newRec);
        if (!normalized?.id) return;

        setRecordsList((prev) => _.map(prev, (record) => (
            `${record.id}` === `${normalized.id}` ? normalized : record
        )));
    };

    const handleRecordAdd = (newRec) => {
        const normalized = normalizeDeviceResponse(newRec);
        if (!normalized?.id) return;

        setRecordsList((prev) => _.orderBy(_.uniqBy([normalized, ...prev], "id"), ["store_id", "zone", "name"], ["asc", "asc", "asc"]));
    };

    const applySearch = useCallback((newVal) => {
        setCurrentPage(1);
        setSearchQuery(newVal);
        setListLoading(true);
    }, []);

    const debouncedSearchQueryChange = useMemo(() => _.debounce(applySearch, 350), [applySearch]);

    useEffect(() => {
        return () => {
            debouncedSearchQueryChange.cancel();
        };
    }, [debouncedSearchQueryChange]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setCurrentPage(1);
        setListLoading(true);
    };

    const hasActiveFilters = Boolean(searchInput || filters.storeId || filters.zone || filters.kind);

    const clearFilters = () => {
        debouncedSearchQueryChange.cancel();
        setSearchInput("");
        setSearchQuery("");
        setFilters({ storeId: "", zone: "", kind: "" });
        setCurrentPage(1);
        setListLoading(true);
    };

    const devicesByStoreCount = useMemo(() => {
        return _.uniq(_.compact(_.map(recordsList, (record) => record?.store?.id || record?.store_id))).length;
    }, [recordsList]);

    const renderRecordsList = () => {
        if (!recordsList.length) {
            return (
                <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="h6" mb={1}>No hay dispositivos para mostrar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Crea un dispositivo nuevo o ajusta los filtros.
                    </Typography>
                </Paper>
            );
        }

        return (
            <Grid container spacing={2.5}>
                {_.map(recordsList, (record) => (
                    <Grid item xs={12} md={6} lg={4} xl={3} key={`device-${record.id}`}>
                        <DeviceCard
                            device={record}
                            showLocation
                            storeName={record?.store?.name}
                            zoneName={record?.zone}
                            onOpen={(device) => openDeviceDetail(device?.id)}
                            onEditClick={(deviceId) => { setCurrentDeviceID(deviceId); setOpenRecordEditModal(true); }}
                            onAlertClick={(device) => navigate(`/automations/rules?device_id=${device?.id || record?.id}`)}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <div>
            <DeviceDetailPanel
                deviceId={openedDeviceId}
                onClose={closeDeviceDetail}
                onEditDevice={(deviceId) => { setCurrentDeviceID(deviceId); setOpenRecordEditModal(true); }}
                onOpenAlerts={(device) => navigate(`/automations/rules?device_id=${device?.id}`)}
            />
            <DeviceEditModal
                recordID={currentDeviceID}
                isOpen={openRecordEditModal}
                zoneOptions={existingZoneOptions}
                onClose={() => { setOpenRecordEditModal(false); }}
                onRecordChange={handleRecordChange}
                onRecordAdd={handleRecordAdd}
            />

            <StandardPageHeader
                title="Dispositivos"
                subtitle={`${totalCount} dispositivos en ${devicesByStoreCount} locales.`}
            />

            <StandardFiltersBar>
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <StandardSearchField
                            fullWidth
                            placeholder="Buscar por nombre, tipo o zona"
                            value={searchInput}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchInput(value);
                                debouncedSearchQueryChange(value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    debouncedSearchQueryChange.cancel();
                                    applySearch(e.currentTarget.value);
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Local</InputLabel>
                            <Select
                                label="Local"
                                value={filters.storeId}
                                onChange={(e) => handleFilterChange("storeId", e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {_.map(filterOptions.stores, (store) => (
                                    <MenuItem key={`store-filter-${store.id}`} value={`${store.id}`}>
                                        {store.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Zona</InputLabel>
                            <Select
                                label="Zona"
                                value={filters.zone}
                                onChange={(e) => handleFilterChange("zone", e.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {_.map(filterOptions.zones, (zone) => (
                                    <MenuItem key={`zone-filter-${zone}`} value={zone}>
                                        {zone}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                label="Tipo"
                                value={filters.kind}
                                onChange={(e) => handleFilterChange("kind", e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {_.map(filterOptions.kinds, (kind) => (
                                    <MenuItem key={`kind-filter-${kind}`} value={kind}>
                                        {kind}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={8} md={1}>
                        <Button
                            fullWidth
                            size="small"
                            variant="text"
                            disabled={!hasActiveFilters}
                            onClick={clearFilters}
                        >
                            Limpiar
                        </Button>
                    </Grid>

                    {canCreateDevice && (
                        <Grid item xs={12} sm={4} md={1}>
                            <Tooltip title="Crear dispositivo">
                                <IconButton
                                    onClick={() => { setCurrentDeviceID(null); setOpenRecordEditModal(true); }}
                                    sx={{ width: 40, height: 40 }}
                                >
                                    <AddCircleOutlineOutlinedIcon color="success" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>
            </StandardFiltersBar>

            <Div sx={{ display: listLoading ? "block" : "none", textAlign: "center", my: 5 }}>
                <CircularProgress />
            </Div>

            {!listLoading && renderRecordsList()}

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => {
                    setCurrentPage(page);
                    setListLoading(true);
                }}
            />
        </div>
    );
};

export default DevicesIndex;
